import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { ChevronLeft, ChevronRight, Languages, RotateCcw, Shield, Trash2 } from "lucide-react";
import { Link } from "react-router";
import { Capacitor } from "@capacitor/core";

import { QueryError, QueryLoading } from "../../components/query/QueryStatus";
import { TransientStatusBanner } from "../../components/feedback/TransientStatusBanner";
import { useTransientMessage } from "../../components/feedback/useTransientMessage";
import { TAB_BODY_COMPACT_TIMEFRAME_CLASS, TAB_TITLE_CLASS } from "../../components/navigation/TabPageHeader";
import { EdgeSwipeBackLayer } from "../../components/navigation/EdgeSwipeBackLayer";
import { TabPageShell } from "../../components/navigation/TabPageShell";
import { LanguageSettingsModal, useScreen0Language } from "@/features/language";
import {
  clearAllOptimisticEnforcementRules,
  clearEnforcementProjection,
} from "@/features/rules/enforcementOptimistic";
import { formatTemplate, getTranslations } from "@/i18n";
import { mutationRef, queryRef } from "../../lib/convex/refs";
import { resolveQueryState } from "../../lib/convex/useQueryState";
import { privacyUrl, termsUrl } from "@/lib/env";
import { useDeviceId, useResetDeviceId } from "../../lib/device/DeviceProvider";
import { restorePurchases } from "@/lib/purchases/revenuecat";
import { markPremiumExplicitlyUnlocked } from "@/lib/purchases/premiumUnlockStorage";
import { getRestoreNothingFoundMessage } from "@/lib/purchases/restoreMessaging";
import { useMarketingDemoMode } from "../../lib/marketing/MarketingDemoProvider";
import { canRequestUsagePermission, resolveLivePermission, useUsageAccess } from "../../lib/native/usageAccess";
import { applyNativeRules } from "../../lib/native/rules";
import { syncNativeWebFilter } from "../../lib/native/webFilter";
import { getScreen0LanguageLabel } from "@/features/language/language-registry";
import { LegalRouteLink } from "@/pages/legal/LegalRouteLink";

type SettingsState = {
  platform: "ios" | "android" | "web";
  permissionStatus: "granted" | "denied" | "unavailable";
  collectionCapabilities: {
    totalScreenTime: boolean;
    topApps: boolean;
    unlockCount: boolean;
    trends: boolean;
  };
  lastSeenAt?: number;
  retentionDays: number;
};

function resolvePermissionDetail(
  permission: string,
  permissionReady: boolean,
  isIos: boolean,
  t: ReturnType<typeof getTranslations>,
) {
  if (!permissionReady && isIos) return t.common.checking;
  if (permission === "granted") return t.permissionStatus.granted;
  if (permission === "denied") return t.permissionStatus.denied;
  if (permission === "notDetermined") return t.permissionStatus.notDetermined;
  return t.permissionStatus.unavailable;
}

export function SettingsTab({ onCollectUsage }: { onCollectUsage?: () => Promise<unknown> }) {
  const { languageCode, languageLabel } = useScreen0Language();
  const t = getTranslations(languageCode);
  const anonymousDeviceId = useDeviceId();
  const resetDeviceId = useResetDeviceId();
  const { isEligible: marketingDemoEligible, enabled: marketingDemoEnabled, setEnabled: setMarketingDemoEnabled, disable: disableMarketingDemo, ready: marketingDemoReady } = useMarketingDemoMode();
  const { permission: livePermission, permissionReady, requiresEntitlement, requestPermission } = useUsageAccess();
  const [permissionBusy, setPermissionBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const {
    message: resetMessage,
    variant: resetMessageVariant,
    show: showResetMessage,
    dismiss: dismissResetMessage,
  } = useTransientMessage();
  const {
    message: restoreMessage,
    variant: restoreMessageVariant,
    show: showRestoreMessage,
    dismiss: dismissRestoreMessage,
  } = useTransientMessage();
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const raw = useQuery(queryRef<{ anonymousDeviceId: string }, SettingsState>("usage:getSettingsState"), { anonymousDeviceId });
  const queryState = useMemo(() => resolveQueryState(raw), [raw]);
  const resetUsageData = useMutation(mutationRef<{ anonymousDeviceId: string }, { deleted: number }>("usage:resetDeviceData"));

  async function handleRequestPermission() {
    setPermissionBusy(true);
    try {
      const state = await requestPermission();
      if (state.status === "granted") await onCollectUsage?.();
    } finally {
      setPermissionBusy(false);
    }
  }

  async function handleReset() {
    setResetBusy(true);
    dismissResetMessage();
    try {
      const result = await resetUsageData({ anonymousDeviceId });
      await disableMarketingDemo();
      await resetDeviceId();
      clearAllOptimisticEnforcementRules();
      clearEnforcementProjection();
      await applyNativeRules([], []);
      await syncNativeWebFilter([], []);
      showResetMessage(formatTemplate(t.settings.resetSuccess, { count: result.deleted }));
    } catch (error) {
      showResetMessage(error instanceof Error ? error.message : t.settings.resetFailed, "error");
    } finally {
      setResetBusy(false);
    }
  }

  async function handleRestore() {
    setRestoreBusy(true);
    dismissRestoreMessage();
    const purchase = t.onboarding.paywall.purchase;

    // Development: don't hit the store; report the neutral "nothing found" line.
    if (import.meta.env.DEV) {
      console.info("[settings] restore skipped in DEV");
      showRestoreMessage(getRestoreNothingFoundMessage(purchase), "info");
      setRestoreBusy(false);
      return;
    }

    try {
      const outcome = await restorePurchases(anonymousDeviceId);
      switch (outcome.status) {
        case "restored":
          await markPremiumExplicitlyUnlocked();
          showRestoreMessage(purchase.restoreSuccess, "success");
          break;
        case "nothing_to_restore":
          showRestoreMessage(getRestoreNothingFoundMessage(purchase), "info");
          break;
        case "unsupported":
          showRestoreMessage(purchase.webUnavailable, "error");
          break;
        case "error":
          showRestoreMessage(purchase.restoreError, "error");
          break;
      }
    } finally {
      setRestoreBusy(false);
    }
  }

  if (queryState.status === "loading") {
    return (
      <SettingsSwipeShell>
        <TabPageShell className={TAB_BODY_COMPACT_TIMEFRAME_CLASS} fixed={<SettingsFixedHeader t={t} />}>
          <QueryLoading label={t.common.loadingSettings} />
        </TabPageShell>
      </SettingsSwipeShell>
    );
  }

  if (queryState.status === "error") {
    return (
      <SettingsSwipeShell>
        <TabPageShell className={TAB_BODY_COMPACT_TIMEFRAME_CLASS} fixed={<SettingsFixedHeader t={t} />}>
          <QueryError message={queryState.message} />
        </TabPageShell>
      </SettingsSwipeShell>
    );
  }

  const state = queryState.status === "data" ? queryState.data : null;
  const permission = resolveLivePermission(permissionReady, livePermission, state?.permissionStatus ?? "unavailable");
  const platform = Capacitor.getPlatform();
  const isAndroid = platform === "android";
  const canRequestPermission = canRequestUsagePermission(
    permissionReady,
    livePermission,
    requiresEntitlement,
    state?.permissionStatus ?? "unavailable",
    platform,
  );
  const permissionDetail = resolvePermissionDetail(
    permission,
    permissionReady,
    platform === "ios",
    t,
  );
  const requestAccessLabel = isAndroid
    ? permissionBusy
      ? t.settings.openingUsageAccess
      : t.settings.openUsageAccess
    : permissionBusy
      ? t.settings.requestingAccess
      : t.settings.requestScreenTimeAccess;

  return (
    <>
      <SettingsSwipeShell>
        <TabPageShell className={TAB_BODY_COMPACT_TIMEFRAME_CLASS} fixed={<SettingsFixedHeader t={t} />}>
        <p className="text-sm font-semibold uppercase text-[oklch(64%_0.04_155)]">{t.settings.controlsEyebrow}</p>

        <Section title={isAndroid ? t.settings.screenTimeSectionAndroid : t.settings.screenTimeSection}>
          <Row icon={<Shield size={18} />} label={t.settings.permissionLabel} detail={permissionDetail} />
          {canRequestPermission ? (
            <button className="tap-row flex w-full items-center justify-between text-left disabled:opacity-60" disabled={permissionBusy} onClick={() => void handleRequestPermission()}>
              <span>{requestAccessLabel}</span>
              <ChevronRight size={18} />
            </button>
          ) : null}
        </Section>

        <Section title={t.language.eyebrow}>
          <button
            type="button"
            className="tap-row flex w-full items-center justify-between text-left"
            onClick={() => setLanguageModalOpen(true)}
          >
            <span className="flex items-center gap-3">
              <Languages size={18} />
              {t.settings.languageRow}
            </span>
            <span className="text-sm text-[oklch(66%_0.02_155)]">{languageLabel ?? getScreen0LanguageLabel(languageCode)}</span>
          </button>
        </Section>

        {marketingDemoEligible && marketingDemoReady ? (
          <Section title="Internal">
            <MarketingDemoToggleRow
              enabled={marketingDemoEnabled}
              onChange={(next) => void setMarketingDemoEnabled(next)}
            />
          </Section>
        ) : null}

        <Section title={t.settings.purchasesSection}>
          <button
            className="tap-row flex w-full items-center gap-3 text-left disabled:opacity-60"
            disabled={restoreBusy}
            onClick={() => void handleRestore()}
          >
            <RotateCcw size={18} />
            <span>
              {restoreBusy
                ? t.onboarding.paywall.purchase.restoreProcessing
                : t.onboarding.paywall.purchase.restorePurchases}
            </span>
          </button>
          <TransientStatusBanner
            message={restoreMessage}
            variant={restoreMessageVariant}
            onDismiss={dismissRestoreMessage}
            dismissLabel={t.common.dismiss}
          />
        </Section>

        <Section title={t.settings.legalSection}>
          <LegalRouteLink href={privacyUrl} className="tap-row flex w-full items-center justify-between text-left">
            <span>{t.settings.privacyPolicyRow}</span>
            <ChevronRight size={18} />
          </LegalRouteLink>
          <LegalRouteLink href={termsUrl} className="tap-row flex w-full items-center justify-between text-left">
            <span>{t.settings.termsRow}</span>
            <ChevronRight size={18} />
          </LegalRouteLink>
        </Section>

        <Section title={t.settings.resetSection}>
          <button className="tap-row flex w-full items-center gap-3 text-left text-[oklch(78%_0.11_35)] disabled:opacity-60" disabled={resetBusy} onClick={() => void handleReset()}>
            {resetBusy ? <RotateCcw size={18} /> : <Trash2 size={18} />}
            <span>{resetBusy ? t.settings.deletingSavedData : t.settings.deleteSavedData}</span>
          </button>
          <TransientStatusBanner
            message={resetMessage}
            variant={resetMessageVariant}
            onDismiss={dismissResetMessage}
            dismissLabel={t.common.dismiss}
          />
        </Section>

        <footer className="pb-2 text-center text-xs leading-6 text-[oklch(58%_0.02_155)]">
          {t.settings.footerNoAccount}
        </footer>
        </TabPageShell>
      </SettingsSwipeShell>
      <LanguageSettingsModal isOpen={languageModalOpen} onClose={() => setLanguageModalOpen(false)} />
    </>
  );
}

function SettingsSwipeShell({ children }: { children: React.ReactNode }) {
  return (
    <EdgeSwipeBackLayer backTarget={{ pathname: "/today" }} className="min-h-[100svh]">
      {children}
    </EdgeSwipeBackLayer>
  );
}

function SettingsFixedHeader({ t }: { t: ReturnType<typeof getTranslations> }) {
  return (
    <header className="grid grid-cols-[auto_1fr] items-center gap-3">
      <Link className="icon-button" to="/today" aria-label={t.common.backToToday}>
        <ChevronLeft size={20} />
      </Link>
      <h1 className={TAB_TITLE_CLASS}>{t.settings.title}</h1>
    </header>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-2">
      <h2 className="px-1 text-xs font-semibold uppercase text-[oklch(64%_0.04_155)]">{title}</h2>
      <div className="liquid-glass-panel px-4">{children}</div>
    </section>
  );
}

function Row({ label, detail, icon }: { label: string; detail?: string; icon?: React.ReactNode }) {
  return (
    <div className="tap-row flex items-center justify-between gap-4">
      <span className="flex items-center gap-3">{icon}{label}</span>
      {detail ? <span className="text-sm capitalize text-[oklch(66%_0.02_155)]">{detail}</span> : null}
    </div>
  );
}

function MarketingDemoToggleRow({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="tap-row flex items-center justify-between gap-4">
      <div className="grid gap-0.5">
        <span className="font-medium">Marketing demo mode</span>
        <span className="text-sm text-[oklch(66%_0.02_155)]">Shows fictitious history for screenshots.</span>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label="Marketing demo mode"
        className={`relative h-8 w-14 shrink-0 rounded-full transition-colors ${enabled ? "bg-[oklch(72%_0.09_155)]" : "bg-[oklch(32%_0.02_155)]"}`}
        onClick={() => onChange(!enabled)}
      >
        <span
          className={`absolute top-1 left-1 h-6 w-6 rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-0"}`}
        />
      </button>
    </div>
  );
}
